document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-support-hub]').forEach((hub) => {
    const gallery = hub.querySelector('[data-support-product-gallery]');
    const productRow = gallery?.querySelector('[data-support-product-row]');
    const productControls = gallery?.querySelector('[data-support-product-controls]');
    const previousButton = gallery?.querySelector('[data-support-product-prev]');
    const nextButton = gallery?.querySelector('[data-support-product-next]');

    if (productRow && productControls && previousButton && nextButton) {
      const updateProductControls = () => {
        const maxScroll = Math.max(0, productRow.scrollWidth - productRow.clientWidth);
        productControls.hidden = maxScroll < 1;
        previousButton.disabled = productRow.scrollLeft <= 1;
        nextButton.disabled = productRow.scrollLeft >= maxScroll - 1;
      };

      const scrollProducts = (direction) => {
        const gap = parseFloat(getComputedStyle(productRow).columnGap) || 0;
        productRow.scrollBy({ left: direction * (productRow.clientWidth + gap), behavior: 'smooth' });
      };

      previousButton.addEventListener('click', () => scrollProducts(-1));
      nextButton.addEventListener('click', () => scrollProducts(1));
      productRow.addEventListener('scroll', updateProductControls, { passive: true });
      window.addEventListener('resize', updateProductControls);
      updateProductControls();
    }

    const searchBox = hub.querySelector('[data-support-search-box]');
    const input = searchBox?.querySelector('[data-support-search]');
    const clearButton = searchBox?.querySelector('[data-support-search-clear]');
    const panel = searchBox?.querySelector('[data-support-search-panel]');
    const results = searchBox?.querySelector('[data-support-results]');
    const status = searchBox?.querySelector('[data-support-search-status]');
    const empty = searchBox?.querySelector('[data-support-empty]');
    const hero = hub.querySelector('.sh-hero');

    if (!searchBox || !input || !clearButton || !panel || !results || !status || !empty) return;

    const normalize = (value) => value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();

    const records = [...hub.querySelectorAll('[data-support-searchable]')]
      .filter((element) => (element.dataset.searchUrl || element.href) && element.dataset.searchTitle)
      .map((element, order) => ({
        element,
        order,
        url: element.dataset.searchUrl || element.href,
        title: element.dataset.searchTitle.trim(),
        category: element.dataset.searchCategory?.trim() || 'Support',
        description: element.dataset.searchDescription?.trim() || '',
        searchable: normalize(element.dataset.supportSearchable || ''),
      }));

    const maxResults = Number.parseInt(searchBox.dataset.maxResults, 10) || 6;
    let activeIndex = -1;
    let visibleResults = [];

    const closeSearch = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      hero?.classList.remove('sh-search-open');
      activeIndex = -1;
    };

    const openSearch = () => {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      hero?.classList.add('sh-search-open');
    };

    const setActiveResult = (index) => {
      const options = [...results.querySelectorAll('[role="option"]')];
      if (!options.length) return;

      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        const isActive = optionIndex === activeIndex;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', String(isActive));
      });

      const activeOption = options[activeIndex];
      input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    };

    const scoreRecord = (record, query, tokens) => {
      if (!tokens.every((token) => record.searchable.includes(token))) return -1;

      const title = normalize(record.title);
      let score = 0;
      if (title === query) score += 200;
      if (title.startsWith(query)) score += 120;
      else if (title.includes(query)) score += 80;
      tokens.forEach((token) => {
        if (title.startsWith(token)) score += 25;
        else if (title.includes(token)) score += 15;
      });
      return score;
    };

    const createResult = (record, index) => {
      const link = document.createElement('a');
      const content = document.createElement('span');
      const category = document.createElement('small');
      const title = document.createElement('strong');
      const arrow = document.createElement('span');

      link.className = 'sh-search-result';
      link.href = record.url;
      link.id = `${input.id}-option-${index}`;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      category.className = 'sh-search-result__category';
      category.textContent = record.category;
      title.textContent = record.title;
      content.append(category, title);

      if (record.description) {
        const description = document.createElement('span');
        description.className = 'sh-search-result__description';
        description.textContent = record.description;
        content.append(description);
      }

      arrow.className = 'sh-search-result__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      link.append(content, arrow);
      link.addEventListener('mouseenter', () => setActiveResult(index));
      return link;
    };

    const renderSearch = () => {
      const query = normalize(input.value);
      clearButton.hidden = !query;
      results.replaceChildren();
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;

      if (!query) {
        visibleResults = [];
        status.textContent = '';
        empty.hidden = true;
        closeSearch();
        return;
      }

      const tokens = query.split(' ').filter(Boolean);
      visibleResults = records
        .map((record) => ({ record, score: scoreRecord(record, query, tokens) }))
        .filter((match) => match.score >= 0)
        .sort((first, second) => second.score - first.score || first.record.order - second.record.order)
        .slice(0, maxResults)
        .map((match) => match.record);

      visibleResults.forEach((record, index) => results.append(createResult(record, index)));
      status.textContent = visibleResults.length === 1 ? '1 result' : `${visibleResults.length} results`;
      empty.hidden = visibleResults.length !== 0;
      results.hidden = visibleResults.length === 0;
      openSearch();
    };

    input.addEventListener('input', renderSearch);
    input.addEventListener('focus', () => {
      if (normalize(input.value)) renderSearch();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeSearch();
        return;
      }
      if (!visibleResults.length || panel.hidden) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveResult(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveResult(activeIndex - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const selectedIndex = activeIndex >= 0 ? activeIndex : 0;
        results.querySelectorAll('[role="option"]')[selectedIndex]?.click();
      }
    });

    clearButton.addEventListener('click', () => {
      input.value = '';
      renderSearch();
      input.focus();
    });

    document.addEventListener('pointerdown', (event) => {
      if (!searchBox.contains(event.target)) closeSearch();
    });
  });
});
